import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import {
  DEFAULT_HTTP_PROXY_PORT,
  DEFAULT_HTTPS_PROXY_PORT,
  DEFAULT_UPSTREAM_PROXY_PORT,
} from '../constants/proxy-port.constants';
import { coerceProxyPort } from '../electron-shared/utils/proxy.util';
import {
  invalidateCacheEntry,
  type RequestBody,
} from '../hooks/useRequestBodies';
import type { HistoryFilters } from '../electron/search-db';
import type {
  HttpRequest,
  HighlightColor,
  HttpRequestResponseUpdate,
} from '../types/http-request';
import { mergeMatchReplaceRulesFromServer as mergeMatchReplaceRulesFromServerUtil } from '../utils/match-replace-rules-merge';
import type { TargetTypeType } from '@terra/shared-schemas-orchestrator';
import type { ApplicationSettingsTabId } from './application-settings-tab-id';

export { type RequestBody };
export type { HttpRequest, HighlightColor, HttpRequestResponseUpdate };

type RequestAnnotationFallback = HttpRequest;

function deleteBodyDbEntries(ids: string[]): void {
  if (ids.length === 0) return;
  window.electronAPI?.bodyDb
    ?.delete(ids)
    ?.catch((err: unknown) =>
      console.warn('Failed to delete body DB entries:', err),
    );
}

function clearBodyDb(): void {
  window.electronAPI?.bodyDb
    ?.clear()
    ?.catch((err: unknown) => console.warn('Failed to clear body DB:', err));
}

export interface InterceptedRequest {
  id: string;
  type: 'request' | 'response'; // Distinguish between requests and responses
  request: HttpRequest;
  requestText: string; // Editable text representation (for requests)
  originalRequestText?: string; // Original request text to detect modifications
  responseText?: string; // Editable text representation (for responses)
  originalResponseText?: string; // Original response text to detect modifications
  timestamp: Date;
  action?: 'forward' | 'drop' | 'modify';
  status?: 'pending' | 'modified' | 'resumed' | 'dropped';
}

export interface InterceptRule {
  id: string;
  name: string;
  urlPattern: string;
  methods: string[];
  enabled: boolean;
  description?: string;
}

export const MATCH_REPLACE_ORIGINS = ['proxy', 'coder', 'voyager'] as const;

export type MatchReplaceOrigin = (typeof MATCH_REPLACE_ORIGINS)[number];

export interface MatchReplaceRule {
  id: string;
  name: string;
  enabled: boolean;
  description?: string;

  matchType:
    | 'url'
    | 'header'
    | 'body'
    | 'statusCode'
    | 'method'
    | 'addHeader'
    | 'removeHeader'
    | 'paramName'
    | 'paramValue'
    | 'firstLine';
  matchLocation: 'request' | 'response' | 'both';
  matchPattern: string;
  isRegex: boolean;
  caseSensitive: boolean;

  headerName?: string;

  replaceWith: string;
  replaceType: 'string' | 'regex' | 'function';
  applyTo?: MatchReplaceOrigin[];

  conditions?: {
    urlPattern?: string;
    methods?: string[];
    statusCodes?: number[];
    contentTypes?: string[];
  };

  createdAt: Date;
  updatedAt: Date;
}

export type MatchReplaceRuleStats = {
  matchCount: number;
  lastUsed?: Date;
};

export type MatchReplacePersistStatus = 'idle' | 'saving' | 'error';

export interface ProxySettings {
  // Server Configuration
  port: number;
  host: string;
  enableHttps: boolean;
  httpsPort: number;

  // SSL/TLS Configuration
  certificatePath?: string;
  keyPath?: string;
  autoGenerateCerts: boolean;
  caCertPath?: string;

  // Intercept Configuration
  interceptEnabled: boolean;
  interceptResponsesEnabled: boolean;
  interceptRules: InterceptRule[];

  // Match and Replace Configuration
  matchReplaceEnabled: boolean;
  matchReplaceRules: MatchReplaceRule[];

  // Upstream Proxy
  upstreamEnabled: boolean;
  upstreamHost?: string;
  upstreamPort?: number;
  upstreamAuth?: {
    username: string;
    password: string;
  };
  validateUpstreamTls: boolean;

  // Request/Response Modification
  modifyHeaders: boolean;
  customHeaders: Record<string, string>;
  removeHeaders: string[];

  // Logging and Storage
  logRequests: boolean;
  logResponses: boolean;
  maxHistorySize: number;
  autoSave: boolean;
  showOnlyInScopeRequests: boolean;

  // Security
  allowedHosts: string[];
  blockedHosts: string[];
  requireAuth: boolean;
  authCredentials?: {
    username: string;
    password: string;
  };

  // Browser Settings
  extensionsEnabled: boolean;
  stealthLaunchEnabled: boolean;
  clientTlsCertificates: ClientTlsCertificateEntry[];
}

export type ClientTlsCertificateEntry = {
  id: string;
  name: string;
  origin: string;
  cert?: string;
  key?: string;
  pfx?: string;
  passphrase?: string;
  enabled: boolean;
  source: 'manual' | 'credential';
  credentialId?: string;
};

const defaultSettings: ProxySettings = {
  port: 8080,
  host: 'localhost',
  enableHttps: true,
  httpsPort: 8443,
  autoGenerateCerts: true,
  interceptEnabled: false,
  interceptResponsesEnabled: false,
  interceptRules: [],
  matchReplaceEnabled: false,
  matchReplaceRules: [],
  upstreamEnabled: false,
  validateUpstreamTls: false,
  modifyHeaders: false,
  customHeaders: {},
  removeHeaders: [],
  logRequests: true,
  logResponses: true,
  maxHistorySize: 1000,
  autoSave: true,
  showOnlyInScopeRequests: false,
  allowedHosts: [],
  blockedHosts: [],
  requireAuth: false,
  extensionsEnabled: true,
  stealthLaunchEnabled: false,
  clientTlsCertificates: [],
};

function sanitizePersistedProxySettings(
  settings: ProxySettings & { enableCopilotProxyTools?: boolean },
): ProxySettings {
  const {
    enableCopilotProxyTools: _removed,
    matchReplaceRules: _matchReplaceRules,
    matchReplaceEnabled: _matchReplaceEnabled,
    interceptRules: _interceptRules,
    ...rest
  } = settings;

  return {
    ...rest,
    matchReplaceRules: [],
    matchReplaceEnabled: false,
    interceptRules: [],
    port: coerceProxyPort({
      port: rest.port,
      fallback: DEFAULT_HTTP_PROXY_PORT,
    }),
    httpsPort: coerceProxyPort({
      port: rest.httpsPort,
      fallback: DEFAULT_HTTPS_PROXY_PORT,
    }),
    upstreamPort: rest.upstreamEnabled
      ? coerceProxyPort({
          port: rest.upstreamPort,
          fallback: DEFAULT_UPSTREAM_PROXY_PORT,
        })
      : rest.upstreamPort,
  };
}

export function partializeProxyStoreForPersist(state: ProxyStore) {
  const {
    matchReplaceRules: _matchReplaceRules,
    matchReplaceEnabled: _matchReplaceEnabled,
    interceptRules: _interceptRules,
    ...persistedSettings
  } = state.settings;

  return {
    settings: persistedSettings,
    activeSettingsTab: state.activeSettingsTab,
    activeApplicationSettingsTab: state.activeApplicationSettingsTab,
    matchReplaceEnabledByTarget: state.matchReplaceEnabledByTarget,
  };
}

export type SettingsTabId =
  | 'general'
  | 'upstream'
  | 'headers'
  | 'security'
  | 'browser'
  | 'client-tls-certificates'
  | 'shortcuts'
  | 'mcp-servers';

export type McpConnectionEditRequest = {
  applicationId: string;
  identityId?: string;
  openEdit: boolean;
  oauthOnly?: boolean;
} | null;

export interface ProxyStore {
  isProxyRunning: boolean;
  isProxyStopping: boolean;
  requests: Record<string, HttpRequest>;
  requestOrder: string[];
  selectedRequest: HttpRequest | null;
  interceptQueue: InterceptedRequest[];
  settings: ProxySettings;
  activeSettingsTab: SettingsTabId;
  activeApplicationSettingsTab: ApplicationSettingsTabId;
  matchReplaceRuleStats: Record<string, MatchReplaceRuleStats>;
  matchReplaceDirtyRuleIds: string[];
  matchReplacePersistStatus: MatchReplacePersistStatus;
  matchReplacePersistError: string | null;
  matchReplaceEnabledByTarget: Record<string, boolean>;
  matchReplaceFocusRuleId: string | null;
  interceptRulesHydrationPending: boolean;
  mcpConnectionEditRequest: McpConnectionEditRequest;

  // Actions
  setProxyRunning: (running: boolean) => void;
  setProxyStopping: (stopping: boolean) => void;
  addRequest: (request: HttpRequest) => void;
  addRequests: (requests: HttpRequest[]) => void;
  updateRequestWithResponse: (
    requestId: string,
    responseData: HttpRequestResponseUpdate,
  ) => void;
  updateRequestsWithResponse: (
    updates: {
      requestId: string;
      responseData: HttpRequestResponseUpdate;
    }[],
  ) => void;
  setSelectedRequest: (request: HttpRequest | null) => void;
  addToInterceptQueue: (request: InterceptedRequest) => void;
  updateInterceptedRequestText: (
    requestId: string,
    requestText: string,
  ) => void;
  updateInterceptedResponseText: (
    requestId: string,
    responseText: string,
  ) => void;
  updateInterceptedRequestStatus: (
    requestId: string,
    status: 'pending' | 'modified' | 'resumed' | 'dropped',
  ) => void;
  updateInterceptedItemToResponse: (
    requestId: string,
    responseData: InterceptedRequest,
  ) => void;
  removeFromInterceptQueue: (requestId: string) => void;
  setRequestProxyError: (requestId: string, error: string) => void;
  addRequestProxyWarning: (requestId: string, warning: string) => void;
  clearRequests: () => void;

  // Settings Actions
  updateSettings: (settings: Partial<ProxySettings>) => void;
  addInterceptRule: (rule: InterceptRule) => void;
  updateInterceptRule: (
    ruleId: string,
    updates: Partial<InterceptRule>,
  ) => void;
  removeInterceptRule: (ruleId: string) => void;
  replaceInterceptRulesForApplication: (
    rules: InterceptRule[],
    options?: { hydrationPending?: boolean },
  ) => void;
  setInterceptRulesHydrationPending: (pending: boolean) => void;

  // Match and Replace Actions
  addMatchReplaceRule: (rule: MatchReplaceRule) => void;
  updateMatchReplaceRule: (
    ruleId: string,
    updates: Partial<MatchReplaceRule>,
  ) => void;
  removeMatchReplaceRule: (ruleId: string) => void;
  removeMatchReplaceRules: (ruleIds: string[]) => void;
  setMatchReplaceRulesEnabled: (params: {
    ruleIds: string[];
    enabled: boolean;
  }) => void;
  duplicateMatchReplaceRule: (ruleId: string) => void;
  reorderMatchReplaceRules: (fromIndex: number, toIndex: number) => void;
  incrementRuleMatchCount: (ruleId: string) => void;
  resetRuleStats: (ruleId: string) => void;
  getMatchReplaceRuleMatchCount: (ruleId: string) => number;
  setMatchReplaceRules: (rules: MatchReplaceRule[]) => void;
  replaceMatchReplaceRulesForApplication: (rules: MatchReplaceRule[]) => void;
  mergeMatchReplaceRulesFromServer: (rules: MatchReplaceRule[]) => void;
  markMatchReplaceRulesDirty: (ruleIds: string[]) => void;
  clearMatchReplaceRulesDirty: () => void;
  setMatchReplacePersistStatus: (params: {
    status: MatchReplacePersistStatus;
    error?: string | null;
  }) => void;
  setMatchReplaceEnabledForTarget: (params: {
    targetScopeKey: string;
    enabled: boolean;
  }) => void;
  applyMatchReplaceEnabledForTarget: (params: {
    targetScopeKey: string;
    enabled: boolean;
  }) => void;
  setMatchReplaceFocusRuleId: (ruleId: string | null) => void;
  setMcpConnectionEditRequest: (request: McpConnectionEditRequest) => void;
  testMatchReplaceRule: (
    rule: MatchReplaceRule,
    testInput: string,
  ) => { matches: boolean; result?: string; error?: string };

  // Import/Export Actions
  exportSettings: () => string;
  importSettings: (settingsJson: string) => void;
  exportMatchReplaceRules: (ruleIds?: string[]) => string;
  importMatchReplaceRules: (rulesJson: string) => void;
  resetSettings: () => void;

  // Annotation Actions
  setRequestHighlight: (
    requestId: string,
    color?: HighlightColor,
    fallbackRequest?: RequestAnnotationFallback,
  ) => void;
  setRequestComment: (requestId: string, comment: string) => void;
  addRequestTag: (requestId: string, tag: string) => void;
  removeRequestTag: (requestId: string, tag: string) => void;
  setRequestTags: (requestId: string, tags: string[]) => void;

  // UI Preferences Actions
  setActiveSettingsTab: (tab: SettingsTabId) => void;
  setActiveApplicationSettingsTab: (tab: ApplicationSettingsTabId) => void;

  // History / AG Grid state
  currentTargetId: string | null;
  currentTargetType: TargetTypeType | null;
  historyVersion: number;
  historyStructureVersion: number;
  historyRowUpdateVersion: number;
  activeFilters: HistoryFilters;
  setCurrentTarget: (params: {
    targetId: string | null;
    targetType: TargetTypeType | null;
  }) => void;
  incrementHistoryVersion: () => void;
  setActiveFilters: (filters: HistoryFilters) => void;
  clearTargetHistory: (params: {
    targetId: string;
    targetType: TargetTypeType;
  }) => Promise<void>;
}

let hydrationResolve: () => void = () => {
  /* placeholder until promise assigns real resolver */
};

const hydrationPromise = new Promise<void>((resolve) => {
  hydrationResolve = resolve;
});

export function waitForHydration(): Promise<void> {
  return hydrationPromise;
}

export const useProxyStore = create<ProxyStore>()(
  persist(
    (set, get) => ({
      isProxyRunning: false,
      isProxyStopping: false,
      requests: {},
      requestOrder: [],
      selectedRequest: null,
      interceptQueue: [],
      settings: defaultSettings,
      activeSettingsTab: 'general',
      activeApplicationSettingsTab: 'general',
      matchReplaceRuleStats: {},
      matchReplaceDirtyRuleIds: [],
      matchReplacePersistStatus: 'idle',
      matchReplacePersistError: null,
      matchReplaceEnabledByTarget: {},
      matchReplaceFocusRuleId: null,
      interceptRulesHydrationPending: false,
      mcpConnectionEditRequest: null,
      currentTargetId: null,
      currentTargetType: null,
      historyVersion: 0,
      historyStructureVersion: 0,
      historyRowUpdateVersion: 0,
      activeFilters: {},

      setProxyRunning: (running) => set({ isProxyRunning: running }),
      setProxyStopping: (stopping) => set({ isProxyStopping: stopping }),

      addRequest: (request) => {
        try {
          const bodySize = request.body?.length || 0;
          const responseBodySize = request.responseBody?.length || 0;

          // Bodies are now stored in SQLite by the main process request handler.
          // Just invalidate the cache so the renderer re-fetches from SQLite.
          invalidateCacheEntry(request.id);

          set((state) => {
            const { requests, requestOrder, settings } = state;
            const existing = requests[request.id];

            const metadataOnly: HttpRequest = {
              ...request,
              body: '',
              responseBody: undefined,
              bodySize,
              responseBodySize,
            };

            if (existing) {
              const merged: HttpRequest = {
                ...existing,
                ...metadataOnly,
                proxyError: request.proxyError ?? existing.proxyError,
                proxyWarnings: request.proxyWarnings ?? existing.proxyWarnings,
                status: request.status ?? existing.status,
                responseHeaders:
                  request.responseHeaders ?? existing.responseHeaders,
                responseHttpVersion:
                  request.responseHttpVersion ?? existing.responseHttpVersion,
                length: request.length ?? existing.length,
                wasProxyModified:
                  request.wasProxyModified || existing.wasProxyModified,
                originalRequest:
                  request.originalRequest ?? existing.originalRequest,
                originalResponse:
                  request.originalResponse ?? existing.originalResponse,
                bodySize: Math.max(bodySize, existing.bodySize || 0),
                responseBodySize: Math.max(
                  responseBodySize,
                  existing.responseBodySize || 0,
                ),
              };

              return {
                requests: {
                  ...requests,
                  [request.id]: merged,
                },
                historyVersion: state.historyVersion + 1,
                historyRowUpdateVersion: state.historyRowUpdateVersion + 1,
              };
            }

            const requestCount = requestOrder.length;
            const excessCount = requestCount - settings.maxHistorySize + 1;

            if (excessCount > 0) {
              const filtered = { ...requests };
              const newOrder = [...requestOrder];
              const evictedIds: string[] = [];
              const diskBodyRefs: string[] = [];

              for (let i = 0; i < excessCount; i++) {
                const oldestId = newOrder.shift();

                if (oldestId) {
                  const evictedReq = requests[oldestId];

                  evictedIds.push(oldestId);
                  if (evictedReq?.bodyRef)
                    diskBodyRefs.push(evictedReq.bodyRef);
                  if (evictedReq?.responseBodyRef)
                    diskBodyRefs.push(evictedReq.responseBodyRef);
                  delete filtered[oldestId];
                }
              }

              if (evictedIds.length > 0) {
                deleteBodyDbEntries(evictedIds);
              }

              if (
                diskBodyRefs.length > 0 &&
                window.electronAPI?.bodyStorage?.delete
              ) {
                Promise.all(
                  diskBodyRefs.map((ref) =>
                    window.electronAPI.bodyStorage.delete(ref),
                  ),
                ).catch((err: unknown) =>
                  console.warn('Failed to clean up disk bodies:', err),
                );
              }

              return {
                requests: {
                  ...filtered,
                  [request.id]: metadataOnly,
                },
                requestOrder: [...newOrder, request.id],
                historyVersion: state.historyVersion + 1,
                historyStructureVersion: state.historyStructureVersion + 1,
              };
            }

            return {
              requests: {
                ...requests,
                [request.id]: metadataOnly,
              },
              requestOrder: [...requestOrder, request.id],
              historyVersion: state.historyVersion + 1,
              historyStructureVersion: state.historyStructureVersion + 1,
            };
          });
        } catch (error) {
          console.error('Failed to add request to store:', error);
        }
      },

      addRequests: (newRequests) => {
        try {
          // Bodies are now stored in SQLite by the main process request handler.
          // Invalidate cache entries so the renderer re-fetches from SQLite.
          newRequests.forEach((req) => invalidateCacheEntry(req.id));

          set((state) => {
            const { settings } = state;
            const requests = { ...state.requests };
            const requestOrder = [...state.requestOrder];
            const maxHistorySize = settings.maxHistorySize;
            let hasStructuralChanges = false;
            let hasRowUpdates = false;

            for (const request of newRequests) {
              const bodySize = request.body?.length || 0;
              const responseBodySize = request.responseBody?.length || 0;
              const existing = requests[request.id];

              const metadataOnly: HttpRequest = {
                ...request,
                body: '',
                responseBody: undefined,
                bodySize,
                responseBodySize,
              };

              if (existing) {
                const merged: HttpRequest = {
                  ...existing,
                  ...metadataOnly,
                  proxyError: request.proxyError ?? existing.proxyError,
                  proxyWarnings:
                    request.proxyWarnings ?? existing.proxyWarnings,
                  status: request.status ?? existing.status,
                  responseHeaders:
                    request.responseHeaders ?? existing.responseHeaders,
                  responseHttpVersion:
                    request.responseHttpVersion ?? existing.responseHttpVersion,
                  length: request.length ?? existing.length,
                  bodySize: Math.max(bodySize, existing.bodySize || 0),
                  responseBodySize: Math.max(
                    responseBodySize,
                    existing.responseBodySize || 0,
                  ),
                };

                requests[request.id] = merged;
                hasRowUpdates = true;
              } else {
                requests[request.id] = metadataOnly;
                requestOrder.push(request.id);
                hasStructuralChanges = true;
              }
            }

            const excessCount = requestOrder.length - maxHistorySize;

            if (excessCount > 0) {
              hasStructuralChanges = true;
              const evictedIds: string[] = [];
              const diskBodyRefs: string[] = [];

              for (let i = 0; i < excessCount; i++) {
                const oldestId = requestOrder.shift();

                if (oldestId) {
                  const evictedReq = requests[oldestId];

                  evictedIds.push(oldestId);
                  if (evictedReq?.bodyRef)
                    diskBodyRefs.push(evictedReq.bodyRef);
                  if (evictedReq?.responseBodyRef)
                    diskBodyRefs.push(evictedReq.responseBodyRef);
                  delete requests[oldestId];
                }
              }

              if (evictedIds.length > 0) {
                deleteBodyDbEntries(evictedIds);
              }

              if (
                diskBodyRefs.length > 0 &&
                window.electronAPI?.bodyStorage?.delete
              ) {
                Promise.all(
                  diskBodyRefs.map((ref) =>
                    window.electronAPI.bodyStorage.delete(ref),
                  ),
                ).catch((err: unknown) =>
                  console.warn('Failed to clean up disk bodies:', err),
                );
              }
            }

            return {
              requests,
              requestOrder,
              historyVersion: state.historyVersion + 1,
              historyStructureVersion: hasStructuralChanges
                ? state.historyStructureVersion + 1
                : state.historyStructureVersion,
              historyRowUpdateVersion: hasRowUpdates
                ? state.historyRowUpdateVersion + 1
                : state.historyRowUpdateVersion,
            };
          });
        } catch (error) {
          console.error('Failed to add requests to store:', error);
        }
      },

      updateRequestWithResponse: (requestId, responseData) => {
        try {
          const responseBodySize = responseData.responseBody?.length || 0;

          // Response bodies are now stored in SQLite by the main process.
          // Invalidate cache so the renderer re-fetches from SQLite.
          invalidateCacheEntry(requestId);

          set((state) => {
            const request = state.requests[requestId];

            if (!request) {
              return {
                historyVersion: state.historyVersion + 1,
                historyStructureVersion: state.historyStructureVersion + 1,
              };
            }

            return {
              requests: {
                ...state.requests,
                [requestId]: {
                  ...request,
                  proxyError: undefined,
                  status: responseData.status,
                  responseStatusMessage: responseData.responseStatusMessage,
                  responseHeaders: responseData.responseHeaders,
                  responseRawHeaders: responseData.responseRawHeaders,
                  length: responseData.length,
                  responseBodySize,
                  responseBodyRef: responseData.responseBodyRef,
                  responseBodyStorageType: responseData.responseBodyStorageType,
                  responseBodyContentType: responseData.responseBodyContentType,
                  responseBodyPreview: responseData.responseBodyPreview,
                  responseHttpVersion: responseData.responseHttpVersion,
                  wasProxyModified:
                    responseData.wasProxyModified || request.wasProxyModified,
                },
              },
              historyVersion: state.historyVersion + 1,
              historyRowUpdateVersion: state.historyRowUpdateVersion + 1,
            };
          });
        } catch (error) {
          console.error('Failed to update request with response:', error);
        }
      },

      updateRequestsWithResponse: (updates) => {
        try {
          // Response bodies are now stored in SQLite by the main process.
          // Invalidate cache entries so the renderer re-fetches from SQLite.
          updates.forEach((u) => invalidateCacheEntry(u.requestId));

          set((state) => {
            const requests = { ...state.requests };
            let hasChanges = false;
            let hasEvictedUpdates = false;

            for (const { requestId, responseData } of updates) {
              const request = requests[requestId];

              if (!request) {
                hasEvictedUpdates = true;
                continue;
              }

              const responseBodySize = responseData.responseBody?.length || 0;

              requests[requestId] = {
                ...request,
                proxyError: undefined,
                status: responseData.status,
                responseStatusMessage: responseData.responseStatusMessage,
                responseHeaders: responseData.responseHeaders,
                responseRawHeaders: responseData.responseRawHeaders,
                length: responseData.length,
                responseBodySize,
                responseBodyRef: responseData.responseBodyRef,
                responseBodyStorageType: responseData.responseBodyStorageType,
                responseBodyContentType: responseData.responseBodyContentType,
                responseBodyPreview: responseData.responseBodyPreview,
                responseHttpVersion: responseData.responseHttpVersion,
                wasProxyModified:
                  responseData.wasProxyModified || request.wasProxyModified,
              };
              hasChanges = true;
            }

            if (!hasChanges && !hasEvictedUpdates) return state;

            return {
              requests: hasChanges ? requests : state.requests,
              historyVersion: state.historyVersion + 1,
              historyStructureVersion: hasEvictedUpdates
                ? state.historyStructureVersion + 1
                : state.historyStructureVersion,
              historyRowUpdateVersion: hasChanges
                ? state.historyRowUpdateVersion + 1
                : state.historyRowUpdateVersion,
            };
          });
        } catch (error) {
          console.error('Failed to update requests with response:', error);
        }
      },

      setSelectedRequest: (request) => set({ selectedRequest: request }),

      setRequestProxyError: (requestId, error) =>
        set((state) => {
          const request = state.requests[requestId];

          if (!request) {
            return state;
          }

          return {
            requests: {
              ...state.requests,
              [requestId]: {
                ...request,
                proxyError: error,
              },
            },
            historyVersion: state.historyVersion + 1,
            historyRowUpdateVersion: state.historyRowUpdateVersion + 1,
          };
        }),

      addRequestProxyWarning: (requestId, warning) =>
        set((state) => {
          const request = state.requests[requestId];

          if (!request) {
            return state;
          }

          const existingWarnings = request.proxyWarnings || [];

          if (existingWarnings.includes(warning)) {
            return state;
          }

          return {
            requests: {
              ...state.requests,
              [requestId]: {
                ...request,
                proxyWarnings: [...existingWarnings, warning],
              },
            },
            historyVersion: state.historyVersion + 1,
            historyRowUpdateVersion: state.historyRowUpdateVersion + 1,
          };
        }),

      addToInterceptQueue: (request) =>
        set((state) => {
          console.log('[proxy-store] addToInterceptQueue called:', {
            id: request.id,
            type: request.type,
            currentQueueSize: state.interceptQueue.length,
          });

          // Check if request already exists in queue (deduplicate by ID)
          const exists = state.interceptQueue.some(
            (req) => req.id === request.id,
          );

          if (exists) {
            console.log(
              '[proxy-store] Request already in queue, skipping:',
              request.id,
            );

            return state; // Don't add duplicate
          }

          const newQueue = [...state.interceptQueue, request];

          console.log('[proxy-store] Added to intercept queue:', {
            id: request.id,
            type: request.type,
            newQueueSize: newQueue.length,
          });

          return {
            interceptQueue: newQueue,
          };
        }),

      updateInterceptedRequestText: (requestId, requestText) =>
        set((state) => ({
          interceptQueue: state.interceptQueue.map((req) =>
            req.id === requestId ? { ...req, requestText } : req,
          ),
        })),

      updateInterceptedResponseText: (requestId, responseText) =>
        set((state) => ({
          interceptQueue: state.interceptQueue.map((req) =>
            req.id === requestId ? { ...req, responseText } : req,
          ),
        })),

      updateInterceptedRequestStatus: (requestId, status) =>
        set((state) => ({
          interceptQueue: state.interceptQueue.map((req) =>
            req.id === requestId ? { ...req, status } : req,
          ),
        })),

      updateInterceptedItemToResponse: (requestId, responseData) =>
        set((state) => ({
          interceptQueue: state.interceptQueue.map((req) =>
            req.id === requestId
              ? {
                  ...responseData,
                  id: req.id, // Keep the original ID
                  status: undefined, // Clear the 'resumed' status
                }
              : req,
          ),
        })),

      removeFromInterceptQueue: (requestId) =>
        set((state) => ({
          interceptQueue: state.interceptQueue.filter(
            (req) => req.id !== requestId,
          ),
        })),

      clearRequests: () => {
        clearBodyDb();

        const electronAPI = window.electronAPI;

        if (electronAPI?.bodyStorage?.clearAll) {
          electronAPI.bodyStorage
            .clearAll()
            .catch((err: unknown) =>
              console.error('Failed to clear body storage:', err),
            );
        }

        return set((state) => ({
          requests: {},
          requestOrder: [],
          selectedRequest: null,
          historyVersion: state.historyVersion + 1,
          historyStructureVersion: state.historyStructureVersion + 1,
        }));
      },

      // Settings Actions
      updateSettings: (newSettings) =>
        set((state) => {
          const headerKeys = [
            'modifyHeaders',
            'customHeaders',
            'removeHeaders',
          ];
          const headerChanges = headerKeys.filter((k) =>
            Object.prototype.hasOwnProperty.call(newSettings, k),
          );
          // Create updated settings
          const updatedSettings = { ...state.settings, ...newSettings };

          // If intercept mode is being changed, immediately notify backend
          const wasEnabled = state.settings.interceptEnabled;
          const isBeingDisabled =
            newSettings.interceptEnabled !== undefined &&
            wasEnabled &&
            !newSettings.interceptEnabled;
          const isBeingEnabled =
            newSettings.interceptEnabled !== undefined &&
            !wasEnabled &&
            newSettings.interceptEnabled;

          if (isBeingDisabled || isBeingEnabled) {
            const pendingCount = state.interceptQueue.length;

            // Immediately notify backend to update intercept mode
            // Don't wait for the debounced useProxyAutoUpdate hook
            if (typeof window !== 'undefined' && window.electronAPI) {
              window.electronAPI.proxy
                .updateConfig({
                  interceptEnabled: newSettings.interceptEnabled,
                })
                .catch((error) => {
                  console.error(
                    '[proxy-store] Failed to notify backend of intercept change:',
                    error,
                  );
                });
            }

            // If disabling intercept, clear the UI queue
            if (isBeingDisabled && pendingCount > 0) {
              console.log(
                '[proxy-store] Intercept disabled - clearing intercept queue',
                { pendingCount },
              );

              return {
                settings: updatedSettings,
                interceptQueue: [], // Clear the UI queue
              };
            }
          }

          if (headerChanges.length > 0) {
            const toggle = updatedSettings.modifyHeaders ?? false;
            const headerModification = {
              enabled: toggle,
              customHeaders: updatedSettings.customHeaders ?? {},
              removeHeaders: updatedSettings.removeHeaders ?? [],
            };

            if (typeof window !== 'undefined' && window.electronAPI) {
              window.electronAPI.proxy
                .updateConfig({ headerModification })
                .then((result) => {
                  if (!result?.success) {
                    console.error(
                      '[proxy-store] updateConfig failed (proxy may not be running):',
                      result?.error,
                    );
                  }
                })
                .catch((error) => {
                  console.error(
                    '[proxy-store] Failed to notify backend of header modification change:',
                    error,
                  );
                });
            }
          }

          // If response intercept mode is being changed, immediately notify backend
          if (newSettings.interceptResponsesEnabled !== undefined) {
            const wasResponseEnabled = state.settings.interceptResponsesEnabled;
            const isResponseInterceptChanging =
              wasResponseEnabled !== newSettings.interceptResponsesEnabled;

            if (isResponseInterceptChanging) {
              console.log(
                '[proxy-store] Response intercept toggled - notifying backend immediately',
                {
                  wasEnabled: wasResponseEnabled,
                  nowEnabled: newSettings.interceptResponsesEnabled,
                },
              );

              // Immediately notify backend to update response intercept mode
              if (typeof window !== 'undefined' && window.electronAPI) {
                window.electronAPI.proxy
                  .updateConfig({
                    interceptResponsesEnabled:
                      newSettings.interceptResponsesEnabled,
                  })
                  .catch((error) => {
                    console.error(
                      '[proxy-store] Failed to notify backend of response intercept change:',
                      error,
                    );
                  });
              }
            }
          }

          return {
            settings: updatedSettings,
          };
        }),

      addInterceptRule: (rule) =>
        set((state) => ({
          settings: {
            ...state.settings,
            interceptRules: [...state.settings.interceptRules, rule],
          },
        })),

      updateInterceptRule: (ruleId, updates) =>
        set((state) => ({
          settings: {
            ...state.settings,
            interceptRules: state.settings.interceptRules.map((rule) =>
              rule.id === ruleId ? { ...rule, ...updates } : rule,
            ),
          },
        })),

      removeInterceptRule: (ruleId) =>
        set((state) => ({
          settings: {
            ...state.settings,
            interceptRules: state.settings.interceptRules.filter(
              (rule) => rule.id !== ruleId,
            ),
          },
        })),

      replaceInterceptRulesForApplication: (rules, options) =>
        set((state) => ({
          settings: {
            ...state.settings,
            interceptRules: rules,
          },
          interceptRulesHydrationPending: options?.hydrationPending ?? false,
        })),

      setInterceptRulesHydrationPending: (pending) =>
        set({ interceptRulesHydrationPending: pending }),

      // Match and Replace Actions
      addMatchReplaceRule: (rule) =>
        set((state) => ({
          settings: {
            ...state.settings,
            matchReplaceRules: [
              ...(state.settings.matchReplaceRules || []),
              rule,
            ],
          },
          matchReplaceDirtyRuleIds: [
            ...new Set([...state.matchReplaceDirtyRuleIds, rule.id]),
          ],
        })),

      updateMatchReplaceRule: (ruleId, updates) =>
        set((state) => ({
          settings: {
            ...state.settings,
            matchReplaceRules: (state.settings.matchReplaceRules || []).map(
              (rule) => (rule.id === ruleId ? { ...rule, ...updates } : rule),
            ),
          },
          matchReplaceDirtyRuleIds: [
            ...new Set([...state.matchReplaceDirtyRuleIds, ruleId]),
          ],
        })),

      removeMatchReplaceRule: (ruleId) =>
        set((state) => {
          const nextStats = { ...state.matchReplaceRuleStats };

          delete nextStats[ruleId];

          return {
            settings: {
              ...state.settings,
              matchReplaceRules: (
                state.settings.matchReplaceRules || []
              ).filter((rule) => rule.id !== ruleId),
            },
            matchReplaceRuleStats: nextStats,
            matchReplaceDirtyRuleIds: [
              ...new Set([...state.matchReplaceDirtyRuleIds, ruleId]),
            ],
          };
        }),

      removeMatchReplaceRules: (ruleIds) =>
        set((state) => {
          const idsToRemove = new Set(ruleIds);
          const nextStats = { ...state.matchReplaceRuleStats };

          for (const id of idsToRemove) {
            delete nextStats[id];
          }

          return {
            settings: {
              ...state.settings,
              matchReplaceRules: (
                state.settings.matchReplaceRules || []
              ).filter((rule) => !idsToRemove.has(rule.id)),
            },
            matchReplaceRuleStats: nextStats,
            matchReplaceDirtyRuleIds: [
              ...new Set([...state.matchReplaceDirtyRuleIds, ...ruleIds]),
            ],
          };
        }),

      setMatchReplaceRulesEnabled: ({ ruleIds, enabled }) =>
        set((state) => ({
          settings: {
            ...state.settings,
            matchReplaceRules: (state.settings.matchReplaceRules || []).map(
              (rule) =>
                ruleIds.includes(rule.id) ? { ...rule, enabled } : rule,
            ),
          },
          matchReplaceDirtyRuleIds: [
            ...new Set([...state.matchReplaceDirtyRuleIds, ...ruleIds]),
          ],
        })),

      duplicateMatchReplaceRule: (ruleId) =>
        set((state) => {
          const rules = state.settings.matchReplaceRules || [];
          const ruleToDuplicate = rules.find((r) => r.id === ruleId);

          if (!ruleToDuplicate) return state;

          const duplicatedId = `${ruleId}-${Date.now()}`;

          return {
            settings: {
              ...state.settings,
              matchReplaceRules: [
                ...rules,
                {
                  ...ruleToDuplicate,
                  id: duplicatedId,
                  name: `${ruleToDuplicate.name} (Copy)`,
                },
              ],
            },
            matchReplaceDirtyRuleIds: [
              ...new Set([...state.matchReplaceDirtyRuleIds, duplicatedId]),
            ],
          };
        }),

      reorderMatchReplaceRules: (fromIndex, toIndex) =>
        set((state) => {
          const rules = [...(state.settings.matchReplaceRules || [])];
          const [movedRule] = rules.splice(fromIndex, 1);

          rules.splice(toIndex, 0, movedRule);

          return {
            settings: {
              ...state.settings,
              matchReplaceRules: rules,
            },
            matchReplaceDirtyRuleIds: [
              ...new Set([
                ...state.matchReplaceDirtyRuleIds,
                ...rules.map((rule) => rule.id),
              ]),
            ],
          };
        }),

      incrementRuleMatchCount: (ruleId) =>
        set((state) => {
          const current = state.matchReplaceRuleStats[ruleId] ?? {
            matchCount: 0,
          };

          return {
            matchReplaceRuleStats: {
              ...state.matchReplaceRuleStats,
              [ruleId]: {
                matchCount: current.matchCount + 1,
                lastUsed: new Date(),
              },
            },
          };
        }),

      resetRuleStats: (ruleId) =>
        set((state) => ({
          matchReplaceRuleStats: {
            ...state.matchReplaceRuleStats,
            [ruleId]: { matchCount: 0 },
          },
        })),

      getMatchReplaceRuleMatchCount: (ruleId) => {
        return get().matchReplaceRuleStats[ruleId]?.matchCount ?? 0;
      },

      setMatchReplaceRules: (rules) =>
        set((state) => ({
          settings: {
            ...state.settings,
            matchReplaceRules: rules,
          },
          matchReplaceDirtyRuleIds: state.matchReplaceDirtyRuleIds.filter(
            (ruleId) => rules.some((rule) => rule.id === ruleId),
          ),
        })),

      replaceMatchReplaceRulesForApplication: (rules) =>
        set((state) => ({
          settings: {
            ...state.settings,
            matchReplaceRules: rules,
          },
          matchReplaceDirtyRuleIds: [],
          matchReplacePersistStatus: 'idle',
          matchReplacePersistError: null,
        })),

      mergeMatchReplaceRulesFromServer: (serverRules) =>
        set((state) => {
          const dirtyRuleIds = new Set(state.matchReplaceDirtyRuleIds);
          const mergedRules = mergeMatchReplaceRulesFromServerUtil({
            localRules: state.settings.matchReplaceRules || [],
            serverRules,
            dirtyRuleIds,
          });

          return {
            settings: {
              ...state.settings,
              matchReplaceRules: mergedRules,
            },
          };
        }),

      markMatchReplaceRulesDirty: (ruleIds) =>
        set((state) => ({
          matchReplaceDirtyRuleIds: [
            ...new Set([...state.matchReplaceDirtyRuleIds, ...ruleIds]),
          ],
        })),

      clearMatchReplaceRulesDirty: () =>
        set({
          matchReplaceDirtyRuleIds: [],
          matchReplacePersistStatus: 'idle',
          matchReplacePersistError: null,
        }),

      setMatchReplacePersistStatus: ({ status, error = null }) =>
        set({
          matchReplacePersistStatus: status,
          matchReplacePersistError: error,
        }),

      setMatchReplaceEnabledForTarget: ({ targetScopeKey, enabled }) =>
        set((state) => ({
          matchReplaceEnabledByTarget: {
            ...state.matchReplaceEnabledByTarget,
            [targetScopeKey]: enabled,
          },
        })),

      applyMatchReplaceEnabledForTarget: ({ targetScopeKey, enabled }) =>
        set((state) => ({
          settings: {
            ...state.settings,
            matchReplaceEnabled: enabled,
          },
          matchReplaceEnabledByTarget: {
            ...state.matchReplaceEnabledByTarget,
            [targetScopeKey]: enabled,
          },
        })),

      setMatchReplaceFocusRuleId: (ruleId) =>
        set({ matchReplaceFocusRuleId: ruleId }),
      setMcpConnectionEditRequest: (request) =>
        set({ mcpConnectionEditRequest: request }),

      testMatchReplaceRule: (rule, testInput) => {
        try {
          const {
            matchPattern,
            replaceWith,
            replaceType,
            isRegex,
            caseSensitive,
          } = rule;

          if (!matchPattern) {
            return { matches: false, error: 'No match pattern provided' };
          }

          // First, test if the pattern matches
          let patternMatches = false;

          if (isRegex || replaceType === 'regex') {
            const flags = caseSensitive ? 'g' : 'gi';

            try {
              const regex = new RegExp(matchPattern, flags);

              patternMatches = regex.test(testInput);
            } catch {
              return { matches: false, error: 'Invalid regex pattern' };
            }
          } else {
            const searchValue = caseSensitive
              ? testInput
              : testInput.toLowerCase();
            const searchPattern = caseSensitive
              ? matchPattern
              : matchPattern.toLowerCase();

            patternMatches = searchValue.includes(searchPattern);
          }

          if (!patternMatches) {
            return { matches: false, result: testInput };
          }

          // Perform the replacement (mirroring MatchReplaceProcessor.performReplacement)
          let result: string;

          if (replaceType === 'function') {
            result = replaceWith;
          } else if (isRegex || replaceType === 'regex') {
            const flags = caseSensitive ? 'g' : 'gi';
            const regex = new RegExp(matchPattern, flags);

            result = testInput.replace(regex, replaceWith);
          } else {
            // Literal string replacement - escape special regex chars
            const escapedPattern = matchPattern.replace(
              /[.*+?^${}()|[\]\\]/g,
              '\\$&',
            );
            const flags = caseSensitive ? 'g' : 'gi';
            const regex = new RegExp(escapedPattern, flags);

            result = testInput.replace(regex, () => replaceWith);
          }

          return { matches: true, result };
        } catch (error) {
          return {
            matches: false,
            error: error instanceof Error ? error.message : 'Unknown error',
          };
        }
      },

      exportSettings: () => {
        const { settings } = get();

        return JSON.stringify(settings, null, 2);
      },

      importSettings: (settingsJson) => {
        try {
          const importedSettings = JSON.parse(settingsJson);

          set(() => ({
            settings: { ...defaultSettings, ...importedSettings },
          }));
        } catch (error) {
          console.error('Failed to import settings:', error);

          throw new Error('Invalid settings format');
        }
      },

      exportMatchReplaceRules: (ruleIds) => {
        const { settings } = get();
        const allRules = settings.matchReplaceRules || [];
        const rulesToExport = ruleIds?.length
          ? allRules.filter((rule) => ruleIds.includes(rule.id))
          : allRules;

        return JSON.stringify(rulesToExport, null, 2);
      },

      importMatchReplaceRules: (rulesJson) => {
        try {
          const importedRules = JSON.parse(rulesJson);

          if (!Array.isArray(importedRules)) {
            throw new Error('Imported data is not an array');
          }

          set((state) => ({
            settings: {
              ...state.settings,
              matchReplaceRules: importedRules,
            },
            matchReplaceDirtyRuleIds: importedRules.map(
              (rule: MatchReplaceRule) => rule.id,
            ),
          }));
        } catch (error) {
          console.error('Failed to import match/replace rules:', error);

          throw new Error('Invalid match/replace rules format');
        }
      },

      resetSettings: () =>
        set(() => ({
          settings: { ...defaultSettings },
        })),

      // Annotation Actions
      setRequestHighlight: (requestId, color, fallbackRequest) =>
        set((state) => {
          const request = state.requests[requestId] ?? fallbackRequest;

          if (!request) return state;

          const updatedRequests = {
            ...state.requests,
            [requestId]: { ...request, highlight: color },
          };

          window.electronAPI?.history?.updateAnnotation({
            id: requestId,
            highlight: color ?? null,
            comment: request?.comment ?? null,
            tags: request?.tags ? JSON.stringify(request.tags) : null,
          });

          return {
            requests: updatedRequests,
            historyVersion: state.historyVersion + 1,
            historyRowUpdateVersion: state.historyRowUpdateVersion + 1,
          };
        }),

      setRequestComment: (requestId, comment) =>
        set((state) => {
          const request = state.requests[requestId];
          const trimmed = comment.trim() || undefined;
          const updatedRequests = request
            ? {
                ...state.requests,
                [requestId]: { ...request, comment: trimmed },
              }
            : state.requests;

          window.electronAPI?.history?.updateAnnotation({
            id: requestId,
            highlight: request?.highlight ?? null,
            comment: trimmed ?? null,
            tags: request?.tags ? JSON.stringify(request.tags) : null,
          });

          return {
            requests: updatedRequests,
            historyVersion: state.historyVersion + 1,
            historyRowUpdateVersion: state.historyRowUpdateVersion + 1,
          };
        }),

      addRequestTag: (requestId, tag) =>
        set((state) => {
          const request = state.requests[requestId];
          const currentTags = request?.tags || [];
          const normalizedTag = tag.trim().toLowerCase();

          if (currentTags.includes(normalizedTag)) return state;

          const newTags = [...currentTags, normalizedTag];
          const updatedRequests = request
            ? {
                ...state.requests,
                [requestId]: { ...request, tags: newTags },
              }
            : state.requests;

          window.electronAPI?.history?.updateAnnotation({
            id: requestId,
            highlight: request?.highlight ?? null,
            comment: request?.comment ?? null,
            tags: JSON.stringify(newTags),
          });

          return {
            requests: updatedRequests,
            historyVersion: state.historyVersion + 1,
            historyRowUpdateVersion: state.historyRowUpdateVersion + 1,
          };
        }),

      removeRequestTag: (requestId, tag) =>
        set((state) => {
          const request = state.requests[requestId];

          if (!request) return state;

          const currentTags = request.tags || [];
          const filteredTags = currentTags.filter((t) => t !== tag);
          const finalTags = filteredTags.length > 0 ? filteredTags : undefined;

          window.electronAPI?.history?.updateAnnotation({
            id: requestId,
            highlight: request.highlight ?? null,
            comment: request.comment ?? null,
            tags: finalTags ? JSON.stringify(finalTags) : null,
          });

          return {
            requests: {
              ...state.requests,
              [requestId]: { ...request, tags: finalTags },
            },
            historyVersion: state.historyVersion + 1,
            historyRowUpdateVersion: state.historyRowUpdateVersion + 1,
          };
        }),

      setRequestTags: (requestId, tags) =>
        set((state) => {
          const request = state.requests[requestId];
          const normalizedTags = tags
            .map((t) => t.trim().toLowerCase())
            .filter((t) => t.length > 0);
          const finalTags =
            normalizedTags.length > 0 ? normalizedTags : undefined;

          const updatedRequests = request
            ? {
                ...state.requests,
                [requestId]: { ...request, tags: finalTags },
              }
            : state.requests;

          window.electronAPI?.history?.updateAnnotation({
            id: requestId,
            highlight: request?.highlight ?? null,
            comment: request?.comment ?? null,
            tags: finalTags ? JSON.stringify(finalTags) : null,
          });

          return {
            requests: updatedRequests,
            historyVersion: state.historyVersion + 1,
            historyRowUpdateVersion: state.historyRowUpdateVersion + 1,
          };
        }),

      // UI Preferences Actions
      setActiveSettingsTab: (tab) => set({ activeSettingsTab: tab }),
      setActiveApplicationSettingsTab: (tab) =>
        set({ activeApplicationSettingsTab: tab }),

      setCurrentTarget: ({ targetId, targetType }) =>
        set({ currentTargetId: targetId, currentTargetType: targetType }),
      incrementHistoryVersion: () =>
        set((state) => ({
          historyVersion: state.historyVersion + 1,
          historyStructureVersion: state.historyStructureVersion + 1,
        })),
      setActiveFilters: (filters) => set({ activeFilters: filters }),
      clearTargetHistory: async ({ targetId, targetType }) => {
        try {
          await window.electronAPI?.history?.clearTarget({
            targetId,
            targetType,
          });
          set((state) => ({
            requests: {},
            requestOrder: [],
            selectedRequest: null,
            historyVersion: state.historyVersion + 1,
            historyStructureVersion: state.historyStructureVersion + 1,
          }));
        } catch (err) {
          console.error('Failed to clear app history:', err);
        }
      },
    }),
    {
      name: 'terra-proxy-store',
      storage: createJSONStorage(() => localStorage),
      partialize: partializeProxyStoreForPersist,
      merge: (persistedState: unknown, currentState: ProxyStore) => {
        if (!persistedState) {
          return currentState;
        }

        const persisted = persistedState as Partial<ProxyStore> & {
          matchReplaceEnabledByApplication?: Record<string, boolean>;
        };

        const rawPersistedSettingsTab = (
          persisted as { activeSettingsTab?: string }
        ).activeSettingsTab;
        const rawPersistedAppSettingsTab = (
          persisted as { activeApplicationSettingsTab?: string }
        ).activeApplicationSettingsTab;
        const persistedAppSettingsTab =
          rawPersistedAppSettingsTab === 'crawler'
            ? 'automation'
            : persisted.activeApplicationSettingsTab;

        const mergedSettings = sanitizePersistedProxySettings(
          persisted.settings
            ? { ...defaultSettings, ...persisted.settings }
            : currentState.settings,
        );

        const persistedStats = currentState.matchReplaceRuleStats;
        const persistedEnabledByTarget =
          persisted.matchReplaceEnabledByTarget ??
          persisted.matchReplaceEnabledByApplication ??
          currentState.matchReplaceEnabledByTarget;

        const migratedEnabledByTarget = Object.fromEntries(
          Object.entries(persistedEnabledByTarget).map(([key, enabled]) => {
            if (key.includes(':')) {
              return [key, enabled];
            }

            return [`WEB_APPLICATION:${key}`, enabled];
          }),
        );

        return {
          ...currentState,
          settings: mergedSettings,
          activeSettingsTab:
            rawPersistedSettingsTab === 'internal-network-tunnel'
              ? 'upstream'
              : persisted.activeSettingsTab || 'general',
          activeApplicationSettingsTab: persistedAppSettingsTab || 'general',
          matchReplaceRuleStats: persistedStats,
          matchReplaceEnabledByTarget: migratedEnabledByTarget as Record<
            string,
            boolean
          >,
        };
      },
      onRehydrateStorage: () => {
        return (state, error) => {
          if (error) {
            console.error('Failed to hydrate proxy store:', error);
          }

          hydrationResolve();
        };
      },
    },
  ),
);
